package dev.terrakok.mapitan.domain.geojson

import androidx.compose.ui.graphics.Color
import dev.terrakok.mapitan.domain.Area
import dev.terrakok.mapitan.domain.Document
import dev.terrakok.mapitan.domain.DocumentObject
import dev.terrakok.mapitan.domain.LineStyle
import dev.terrakok.mapitan.domain.TrackPoint
import dev.terrakok.mapitan.domain.utils.toHexRgb
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.spatialk.geojson.Feature
import org.maplibre.spatialk.geojson.FeatureCollection
import org.maplibre.spatialk.geojson.Geometry
import org.maplibre.spatialk.geojson.LineString
import org.maplibre.spatialk.geojson.Point
import org.maplibre.spatialk.geojson.Polygon
import org.maplibre.spatialk.geojson.Position

data class AreaDraft(
    val points: List<TrackPoint>,
    val color: Color,
    val lineThickness: Int,
    val lineStyle: LineStyle,
)

data class AreaPointDrag(val areaId: String, val pointId: String, val point: Point)

data class AreaPointRef(val areaId: String, val pointId: String)

private fun AreaPointDrag?.pointOf(areaId: String, areaPoint: TrackPoint): Point =
    if (this != null && this.areaId == areaId && pointId == areaPoint.id) point else areaPoint.point

private fun Area.positions(drag: AreaPointDrag?): List<Position> =
    points.map { drag.pointOf(id, it).coordinates }

private fun List<Position>.closed(): List<Position> = ArrayList<Position>(size + 1).also {
    it.addAll(this)
    it.add(first())
}

private fun List<Position>.boundsPolygon(padding: Double = 0.0): Polygon? {
    if (isEmpty()) return null
    val minLongitude = minOf { it.longitude }
    val maxLongitude = maxOf { it.longitude }
    val minLatitude = minOf { it.latitude }
    val maxLatitude = maxOf { it.latitude }
    val longitudePadding = maxOf((maxLongitude - minLongitude) * padding, 0.0001)
    val latitudePadding = maxOf((maxLatitude - minLatitude) * padding, 0.0001)
    return Polygon(
        listOf(
            Position(minLongitude - longitudePadding, minLatitude - latitudePadding),
            Position(minLongitude - longitudePadding, maxLatitude + latitudePadding),
            Position(maxLongitude + longitudePadding, maxLatitude + latitudePadding),
            Position(maxLongitude + longitudePadding, minLatitude - latitudePadding),
            Position(minLongitude - longitudePadding, minLatitude - latitudePadding),
        )
    )
}

private fun areaProperties(
    areaId: String,
    color: Color,
    thickness: Int,
    lineStyle: LineStyle,
): JsonObject = buildJsonObject {
    put(MapKeys.MAPITAN_ID_PROPERTY, JsonPrimitive(areaId))
    put(MapKeys.COLOR_PROPERTY, JsonPrimitive(color.toHexRgb()))
    put(MapKeys.THICKNESS_PROPERTY, JsonPrimitive(thickness))
    put(MapKeys.LINE_STYLE_PROPERTY, JsonPrimitive(lineStyle.name))
}

private fun areaPolygonFeature(
    areaId: String,
    positions: List<Position>,
    color: Color,
    thickness: Int,
    lineStyle: LineStyle,
): Feature<Polygon, JsonObject>? {
    if (positions.size < 3) return null
    return Feature(
        geometry = Polygon(positions.closed()),
        properties = areaProperties(areaId, color, thickness, lineStyle),
    )
}

private fun areaLineFeature(
    areaId: String,
    positions: List<Position>,
    color: Color,
    thickness: Int,
    lineStyle: LineStyle,
): Feature<LineString, JsonObject>? {
    if (positions.size < 2) return null
    return Feature(
        geometry = LineString(positions.closed()),
        properties = areaProperties(areaId, color, thickness, lineStyle),
    )
}

private fun areaPointFeature(
    areaId: String,
    pointId: String,
    point: Point,
    color: Color,
): Feature<Point, JsonObject> = Feature(
    geometry = point,
    properties = buildJsonObject {
        put(MapKeys.MAPITAN_ID_PROPERTY, JsonPrimitive(pointId))
        put(MapKeys.POINT_PARENT_ID_PROPERTY, JsonPrimitive(areaId))
        put(MapKeys.COLOR_PROPERTY, JsonPrimitive(color.toHexRgb()))
    },
)

fun Document.areaPolygonsGeoJson(
    draft: AreaDraft? = null,
    drag: AreaPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            areas.forEach { area ->
                areaPolygonFeature(
                    area.id,
                    area.positions(drag),
                    area.color,
                    area.lineThickness,
                    area.lineStyle,
                )?.let(::add)
            }
            if (draft != null) {
                areaPolygonFeature(
                    MapKeys.DRAFT_AREA_ID,
                    draft.points.map { it.point.coordinates },
                    draft.color,
                    draft.lineThickness,
                    draft.lineStyle,
                )?.let(::add)
            }
        }
    )
)

fun Document.areaLinesGeoJson(
    draft: AreaDraft? = null,
    drag: AreaPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            areas.forEach { area ->
                areaLineFeature(
                    area.id,
                    area.positions(drag),
                    area.color,
                    area.lineThickness,
                    area.lineStyle,
                )?.let(::add)
            }
            if (draft != null) {
                areaLineFeature(
                    MapKeys.DRAFT_AREA_ID,
                    draft.points.map { it.point.coordinates },
                    draft.color,
                    draft.lineThickness,
                    draft.lineStyle,
                )?.let(::add)
            }
        }
    )
)

fun AreaDraft?.draftCursorLineGeoJson(cursor: Position? = null): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            val draft = this@draftCursorLineGeoJson ?: return@buildList
            val last = draft.points.lastOrNull()?.point?.coordinates ?: return@buildList
            if (cursor == null) return@buildList
            val first = draft.points.firstOrNull()?.point?.coordinates ?: return@buildList
            val properties = buildJsonObject {
                put(MapKeys.MAPITAN_ID_PROPERTY, JsonPrimitive(MapKeys.DRAFT_AREA_ID))
                put(MapKeys.COLOR_PROPERTY, JsonPrimitive(draft.color.toHexRgb()))
                put(MapKeys.THICKNESS_PROPERTY, JsonPrimitive(draft.lineThickness))
            }
            add(Feature(geometry = LineString(listOf(first, cursor)), properties = properties))
            if (last != first) {
                add(Feature(geometry = LineString(listOf(last, cursor)), properties = properties))
            }
        }
    )
)

fun Document.areaPointsGeoJson(
    selectedObjectId: String? = null,
    draft: AreaDraft? = null,
    drag: AreaPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            areas.filter { it.id == selectedObjectId }.forEach { area ->
                area.points.forEach { point ->
                    add(areaPointFeature(area.id, point.id, drag.pointOf(area.id, point), area.color))
                }
            }
            draft?.points?.forEach { point ->
                add(areaPointFeature(MapKeys.DRAFT_AREA_ID, point.id, point.point, draft.color))
            }
        }
    )
)

fun List<Feature<Geometry, JsonObject?>>.findAreaPointRef(): AreaPointRef? =
    firstNotNullOfOrNull { feature ->
        val properties = feature.properties ?: return@firstNotNullOfOrNull null
        val areaId = properties[MapKeys.POINT_PARENT_ID_PROPERTY]?.jsonPrimitive?.content
            ?: return@firstNotNullOfOrNull null
        val pointId = properties[MapKeys.MAPITAN_ID_PROPERTY]?.jsonPrimitive?.content
            ?: return@firstNotNullOfOrNull null
        AreaPointRef(areaId, pointId)
    }

fun DocumentObject?.areaSelectionGeoJson(
    draft: AreaDraft? = null,
    drag: AreaPointDrag? = null,
): GeoJsonData = GeoJsonData.Features(
    FeatureCollection(
        buildList {
            val selectedPositions = (this@areaSelectionGeoJson as? Area)?.positions(drag).orEmpty()
            selectedPositions.boundsPolygon(padding = 0.05)?.coordinates?.firstOrNull()?.let {
                add(Feature(LineString(it), buildJsonObject { }))
            }
            val draftPositions = draft?.points?.map { it.point.coordinates }.orEmpty()
            draftPositions.boundsPolygon(padding = 0.05)?.coordinates?.firstOrNull()?.let {
                add(Feature(LineString(it), buildJsonObject { }))
            }
        }
    )
)

fun Area.insertIndexFor(position: Position): Int {
    if (points.size < 2) return points.size
    var bestIndex = 1
    var bestDistance = Double.MAX_VALUE
    points.indices.forEach { index ->
        val nextIndex = (index + 1) % points.size
        val distance = distanceToSegment(
            position,
            points[index].point.coordinates,
            points[nextIndex].point.coordinates,
        )
        if (distance < bestDistance) {
            bestDistance = distance
            bestIndex = if (nextIndex == 0) points.size else nextIndex
        }
    }
    return bestIndex
}
